import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Table, Space, Select, Input, DatePicker, Button, Tag, Segmented, message } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { selectFilter } from '../utils/selectFilter';
import {
  TASK_STATUSES, TASK_STATUS_COLOR, PRIORITY_COLOR, useProjectOptions, TaskDrawer, AppliesToCell, userLabel, projectPath, taskDeleteColumn, repeatColumn,
} from '../components/ProjectParts';

// Quick toggles above the filters (as on Help Desk). Status: the toggle (Open
// by default = Open and In Progress) unless statuses are picked in the
// dropdown, which then win; a toggle click clears the dropdown. Assigned: no
// toggle lit when the dropdown holds a person. Every filter searches at once.
const STATUS_PRESETS = { open: ['Open', 'In Progress'], done: ['Done'], all: [] };
const ASSIGNED_PRESETS = ['any', 'me', 'none', 'me_or_none'];

// Oversight > Project Tasks: every task this user may see (projects and
// folders they are a member of, tasks assigned to them; admins all), with
// filters. Opens in the task drawer.

// The task columns, shared by both views: what matters most first (Status,
// Priority, Due, who), where it lives after. Task stays pinned on the left
// while the rest scrolls.
const TASK_COLS = (today) => [
  { title: 'Task', dataIndex: 'Title', key: 'Title', width: 280, fixed: 'left',
    sorter: (a, b) => a.Title.localeCompare(b.Title) },
  { title: 'Status', dataIndex: 'Status', key: 'Status', width: 110, render: (v) => <Tag color={TASK_STATUS_COLOR[v]}>{v}</Tag> },
  { title: 'Priority', dataIndex: 'Priority', key: 'Priority', width: 90, render: (v) => <Tag color={PRIORITY_COLOR[v]}>{v}</Tag> },
  { title: 'Due', dataIndex: 'DueDate', key: 'DueDate', width: 110,
    sorter: (a, b) => (a.DueDate || '9999').localeCompare(b.DueDate || '9999'),
    render: (v, t) => (v ? <span style={{ color: t.Status !== 'Done' && v < today ? '#ff4d4f' : undefined }}>{v}</span> : '—') },
  { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 170,
    sorter: (a, b) => (a.AssignedToName || '').localeCompare(b.AssignedToName || ''),
    render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
  repeatColumn,
  { title: 'Folder', dataIndex: 'ProjectName', key: 'ProjectName', width: 160,
    sorter: (a, b) => a.ProjectName.localeCompare(b.ProjectName),
    render: (v, t) => <Link to={`/folders/${t.ProjectID}`} onClick={(e) => e.stopPropagation()}>{v}</Link> },
  { title: 'Project', dataIndex: 'FolderName', key: 'FolderName', width: 160,
    render: (v, t) => <Link to={projectPath(t.ProjectID, t.FolderID)} onClick={(e) => e.stopPropagation()}>{v}</Link> },
  // The Project's type, else its Folder's -- the same rule the Type filter uses.
  { title: 'Type', dataIndex: 'TypeName', key: 'TypeName', width: 160,
    sorter: (a, b) => (a.TypeName || '').localeCompare(b.TypeName || ''),
    render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
  { title: 'Applies To', key: 'AppliesTo', width: 280, render: (_, t) => <AppliesToCell task={t} /> },
];

export default function ProjectTasksPage() {
  const options = useProjectOptions();
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMode, setStatusMode] = useState('open');
  const [statuses, setStatuses] = useState([]);
  // Opens on Me + Unassigned; Anyone is a click away.
  const [assigned, setAssigned] = useState('me'); // 'me' | 'none' | 'me_or_none' | user id
  const [projectId, setProjectId] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  // Type: each task's Project's type, else its Folder's.
  const [typeId, setTypeId] = useState(null);
  // An agent's "file": the tasks whose Applies To names them.
  const [appliesAgent, setAppliesAgent] = useState(null);
  const [due, setDue] = useState(null);
  const [q, setQ] = useState('');
  const [taskId, setTaskId] = useState(null);
  // Tasks, or By Applies To: one row per person named in a task's Applies To,
  // their tasks under it -- "what tasks does this person apply to?". The
  // filters apply to both.
  const [view, setView] = useState('tasks');

  useEffect(() => {
    api.get('/api/projects', { params: { archived: 1 } }).then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      ...((statuses.length ? statuses : STATUS_PRESETS[statusMode]).length
        ? { status: (statuses.length ? statuses : STATUS_PRESETS[statusMode]).join(',') } : {}),
      ...(assigned != null ? { assigned_to: assigned } : {}),
      ...(projectId ? { project_id: projectId } : {}),
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(typeId ? { type_id: typeId } : {}),
      ...(appliesAgent ? { applies_agent_id: appliesAgent } : {}),
      ...(due?.[0] ? { due_from: due[0].format('YYYY-MM-DD') } : {}),
      ...(due?.[1] ? { due_to: due[1].format('YYYY-MM-DD') } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    };
    api.get('/api/project-tasks', { params })
      .then((r) => setRows(r.data || []))
      .catch(() => message.error('Failed to load tasks.'))
      .finally(() => setLoading(false));
  }, [statusMode, statuses, assigned, projectId, campaignId, typeId, appliesAgent, due, q]);
  useEffect(() => { load(); }, [statusMode, statuses, assigned, projectId, campaignId, typeId, appliesAgent, due]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = dayjs().format('YYYY-MM-DD');
  const overdue = (t) => t.Status !== 'Done' && t.DueDate && t.DueDate < today;
  // Pinned with Task (pinned columns must sit together on the left).
  const taskCols = [{ ...taskDeleteColumn((t) => t.CanDelete, load), fixed: 'left' }, ...TASK_COLS(today)];

  // By Applies To: each person named in Applies To, with their tasks.
  // Teams, campaigns and jobs are not expanded to their members (that would
  // list a task under everyone on a team); those tasks are counted instead.
  const { groups, groupOnly } = useMemo(() => {
    const by = new Map();
    let only = 0;
    rows.forEach((t) => {
      const agents = (t.AppliesTo || []).filter((a) => a.type === 'agent');
      if (!agents.length && (t.AppliesTo || []).length) only += 1;
      agents.forEach((a) => {
        if (!by.has(a.id)) by.set(a.id, { key: a.id, name: a.name, tasks: [] });
        by.get(a.id).tasks.push(t);
      });
    });
    const list = [...by.values()].map((g) => ({
      ...g,
      total: g.tasks.length,
      open: g.tasks.filter((t) => t.Status !== 'Done').length,
      overdue: g.tasks.filter(overdue).length,
    })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { groups: list, groupOnly: only };
  }, [rows, today]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Project Tasks</Typography.Title>
      <Space wrap size={16} style={{ marginBottom: 12 }}>
        <Segmented
          value={statuses.length ? null : statusMode}
          onChange={(k) => { setStatusMode(k); setStatuses([]); }}
          options={[{ value: 'open', label: 'Open' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' }]}
        />
        <Segmented
          value={ASSIGNED_PRESETS.includes(assigned ?? 'any') ? (assigned ?? 'any') : null}
          onChange={(k) => setAssigned(k === 'any' ? null : k)}
          options={[{ value: 'any', label: 'Anyone' }, { value: 'me', label: 'Assigned to Me' },
                    { value: 'none', label: 'Unassigned' }, { value: 'me_or_none', label: 'Me + Unassigned' }]}
        />
        <Segmented value={view} onChange={setView}
          options={[{ value: 'tasks', label: 'Tasks' }, { value: 'employee', label: 'By Applies To' }]} />
      </Space>
      <br />
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search allowClear placeholder="Search tasks" style={{ width: 220 }} value={q}
          onChange={(e) => setQ(e.target.value)} onSearch={load} />
        <Select mode="multiple" allowClear placeholder="Specific statuses" style={{ minWidth: 200 }} value={statuses}
          onChange={setStatuses} options={TASK_STATUSES.map((s) => ({ value: s, label: s }))} />
        <Select allowClear showSearch filterOption={selectFilter} placeholder="Assigned to anyone" style={{ width: 210 }}
          value={assigned} onChange={(v) => setAssigned(v ?? null)}
          options={[{ value: 'me', label: 'Me' }, { value: 'none', label: 'Unassigned' },
            { value: 'me_or_none', label: 'Me + Unassigned' },
            ...options.users.map((u) => ({ value: u.ID, label: userLabel(u) }))]} />
        <Select allowClear showSearch filterOption={selectFilter} placeholder="All folders" style={{ width: 220 }}
          value={projectId} onChange={setProjectId} options={projects.map((p) => ({ value: p.ID, label: p.Name }))} />
        <Select allowClear showSearch filterOption={selectFilter} placeholder="All campaigns" style={{ width: 200 }}
          value={campaignId} onChange={setCampaignId} options={options.campaigns.map((c) => ({ value: c.ID, label: c.Name }))} />
        <Select allowClear showSearch filterOption={selectFilter} placeholder="Applies to (any agent)" style={{ width: 240 }}
          value={appliesAgent} onChange={setAppliesAgent}
          options={options.agents.map((a) => ({ value: a.ID, label: userLabel(a) }))} />
        <Select allowClear showSearch filterOption={selectFilter} placeholder="All types" style={{ width: 200 }}
          value={typeId} onChange={setTypeId} options={options.types.map((t) => ({ value: t.ID, label: t.Name }))} />
        <DatePicker.RangePicker allowEmpty={[true, true]} placeholder={['Due from', 'Due to']} value={due} onChange={setDue} />
        <Button onClick={load}>Refresh</Button>
      </Space>
      {view === 'tasks' ? (
      <Table
        rowKey="ID" size="small" loading={loading} dataSource={rows}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'], showTotal: (t) => `${t} ${t === 1 ? 'task' : 'tasks'}` }} scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No tasks match.' }}
        onRow={(t) => ({ onClick: () => setTaskId(t.ID), style: { cursor: 'pointer' } })}
        columns={taskCols}
      />
      ) : (
        <>
          {groupOnly > 0 && (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              {groupOnly} matching task{groupOnly === 1 ? '' : 's'} apply to a team, campaign or job rather than a named person, so {groupOnly === 1 ? "it isn't" : "they aren't"} listed here.
            </Typography.Paragraph>
          )}
          <Table
            rowKey="key" size="small" loading={loading} dataSource={groups}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'],
              showTotal: (t) => `${t} ${t === 1 ? 'employee' : 'employees'}` }}
            locale={{ emptyText: 'No matching tasks name a person in Applies To.' }}
            expandable={{
              expandRowByClick: true,
              expandedRowRender: (g) => (
                <Table rowKey="ID" size="small" dataSource={g.tasks} pagination={false} scroll={{ x: 'max-content' }}
                  onRow={(t) => ({ onClick: () => setTaskId(t.ID), style: { cursor: 'pointer' } })}
                  columns={taskCols.filter((c) => c.key !== 'AppliesTo')} />
              ),
            }}
            columns={[
              { title: 'Applies To', dataIndex: 'name', key: 'name',
                sorter: (a, b) => (a.name || '').localeCompare(b.name || '') },
              { title: 'Open', dataIndex: 'open', key: 'open', width: 90, align: 'right', sorter: (a, b) => a.open - b.open },
              { title: 'Overdue', dataIndex: 'overdue', key: 'overdue', width: 100, align: 'right',
                sorter: (a, b) => a.overdue - b.overdue,
                render: (v) => (v ? <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{v}</span> : 0) },
            ]}
          />
        </>
      )}
      <TaskDrawer open={!!taskId} taskId={taskId} onClose={() => setTaskId(null)} onChanged={load} options={options} />
    </div>
  );
}

